/**
 * Platform admins feature — Zod schemas.
 *
 * The "platform admins" feature surfaces every admin_users row
 * across all scopes — platform (tenant_id IS NULL), tenant, and
 * organization. The /admins list page filters to platform-scope
 * rows, but the invite dialog lets a sufficiently-ranked admin
 * invite any role whose rank is ≥ their own.
 *
 * Mirrors the backend AdminUserResponse (pkg/api/admin/adminuser.go)
 * for fields the UI renders; the backend emits several fields with
 * `omitempty`, so Zod uses `.optional()` for those — an empty
 * string on the server is dropped from JSON and arrives as
 * `undefined` on the client.
 */

import { z } from "zod";

// ── Role / status enums ───────────────────────────────────────────────────

/**
 * All 8 RBAC roles — platform, tenant, and organization scope, plus the
 * agent_reconciler service role. Kept identical to the backend enum
 * (internal/admin/rbac/rbac.go). agent_reconciler is a service role (minted as
 * an API key, never a human invite); it is included so admin-list rows or
 * catalog data carrying it never trip the Zod parse. The invite dialog's
 * INVITABLE_ROLE_SET is what gates which roles a human can actually be invited
 * with, so widening this enum does not expose agent_reconciler in the selector.
 */
export const adminRoleSchema = z.enum([
  "platform_admin",
  "platform_operator",
  "tenant_admin",
  "tenant_operator",
  "tenant_readonly",
  "org_admin",
  "org_operator",
  "agent_reconciler",
  // See rbac-schemas.ts. Missing here, this enum rejected every admin list
  // containing a compliance_reader — which is the query ReviewerPicker reads,
  // so one assessor seat emptied the picker and every two-person ceremony
  // reported "this tenant has no other active admin".
  "compliance_reader",
]);
export type AdminRole = z.infer<typeof adminRoleSchema>;

/**
 * Backwards-compatible alias. The /admins list page only renders
 * platform-scope rows today, so PlatformAdmin is a subset of
 * AdminRole — but we reuse the full enum here so the Zod parse
 * of /api/v1/admin-users never trips over a tenant-scoped row
 * that slipped through. A follow-up can tighten this if and when
 * the list endpoint is strictly filtered server-side.
 */
export const platformAdminRoleSchema = adminRoleSchema;
export type PlatformAdminRole = AdminRole;

export const platformAdminStatusSchema = z.enum([
  "pending",
  "active",
  "inactive",
  "expired",
]);
export type PlatformAdminStatus = z.infer<typeof platformAdminStatusSchema>;

// ── Row shape ─────────────────────────────────────────────────────────────

export const platformAdminSchema = z.object({
  id: z.string(),
  tenant_id: z.string().optional(),
  email: z.string(),
  name: z.string(),
  phone: z.string().optional(),
  role: adminRoleSchema,
  status: platformAdminStatusSchema,
  // "human" | "automation_account". Both populations live in admin_users, so a
  // surface that does not read this shows a machine among the people. Caught
  // when the owner picker needed to offer only accountable humans — the API and
  // the database both refuse a non-human owner, so a picker that listed one
  // would offer a choice guaranteed to fail.
  //
  // CAUGHT AND DEFAULTED TO "human", WHICH IS THE PERMISSIVE DIRECTION — and that
  // is deliberate rather than careless, because of WHERE the authority sits.
  //
  // The field is new on the wire, so an older backend omits it. Defaulting the
  // other way (assume machine) would empty the owner picker against such a
  // backend and break the feature outright — a false negative costs the whole
  // capability. Defaulting to human costs at most ONE wrong entry in a picker,
  // and the server refuses it with a named 400 ("Owner must be a person"), so
  // the mistake is caught, explained, and recoverable in one click.
  //
  // This is only safe because the client is not the guard. If the client were
  // ever the thing deciding, the default would have to invert.
  kind: z.enum(["human", "automation_account"]).catch("human").default("human"),
  last_login_at: z.string().optional(),
  deactivated_at: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PlatformAdmin = z.infer<typeof platformAdminSchema>;

export const platformAdminsListResponseSchema = z.object({
  items: z.array(platformAdminSchema),
  next_cursor: z.string().optional(),
});
export type PlatformAdminsListResponse = z.infer<typeof platformAdminsListResponseSchema>;

// ── Invite input ──────────────────────────────────────────────────────────

// Match the admin service's phone format without inferring a country code.
const adminPhonePattern = /^\+[1-9]\d{1,14}$/;

export function normalizeAdminPhone(value: string): string {
  const trimmed = value.trim();
  const compact = trimmed.replace(/[\s().-]/g, "");
  const international = compact.startsWith("+") ? compact : `+${compact}`;
  // Preserve invalid input for field validation; never discard letters or
  // extensions and silently turn them into a different phone number.
  return adminPhonePattern.test(international) ? international : trimmed;
}

/**
 * Invite input. Fields are tied to the backend's
 * CreateAdminUserRequest shape. Cross-field rules enforced via
 * superRefine rather than separate schemas so react-hook-form's
 * SubmitHandler typing stays clean:
 *
 *   - Platform roles require a phone (backend's admin service
 *     rejects activation without one; we fail at form submit so
 *     the operator sees a clear error instead of a server 4xx).
 *   - Tenant / org roles require a tenant_id; the backend rejects
 *     invites with the wrong scope, so we block them client-side.
 */
export const inviteAdminSchema = z
  .object({
    email: z.string().email("Enter a valid email"),
    name: z.string().min(1, "Name is required").max(255),
    phone: z.string().transform(normalizeAdminPhone).refine(
      (value) => value === "" || adminPhonePattern.test(value),
      "Enter a phone number with its country code, for example +14155551234. Use 2–15 digits and no extension.",
    ).optional(),
    role: adminRoleSchema,
    tenant_id: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    const isPlatform = val.role === "platform_admin" || val.role === "platform_operator";
    if (isPlatform && (!val.phone || val.phone.trim() === "")) {
      ctx.addIssue({
        code: "custom",
        path: ["phone"],
        message: "Phone is required for platform-scope roles.",
      });
    }
    if (!isPlatform && (!val.tenant_id || val.tenant_id.trim() === "")) {
      ctx.addIssue({
        code: "custom",
        path: ["tenant_id"],
        message: "Select a tenant for tenant- or organization-scope roles.",
      });
    }
  });
export type InviteAdminInput = z.infer<typeof inviteAdminSchema>;

// Back-compat alias for existing imports.
export const invitePlatformAdminSchema = inviteAdminSchema;
export type InvitePlatformAdminInput = InviteAdminInput;

// ── Invite response ───────────────────────────────────────────────────────

/**
 * Response shape from POST /api/v1/admin-users. The invite
 * dialog uses `enrollment_token` to render the one-time
 * enrollment-link reveal screen after a successful invite.
 */
export const inviteAdminResponseSchema = z.object({
  admin_user: platformAdminSchema,
  enrollment_token: z.string().optional(),
  warnings: z.array(z.string()).optional(),
});
export type InviteAdminResponse = z.infer<typeof inviteAdminResponseSchema>;

// ── Role update ───────────────────────────────────────────────────────────

export const updatePlatformAdminRoleSchema = z.object({
  role: adminRoleSchema,
});
export type UpdatePlatformAdminRoleInput = z.infer<typeof updatePlatformAdminRoleSchema>;
